import { useState, useEffect } from "react";
import NectarApp from "./NectarApp";
import NectarOnboard from "./NectarOnboard";
import NectarLanding from "./NectarLanding";
import NectarAdmin from "./NectarAdmin";
import InviteOnboarding from "./components/workspace/InviteOnboarding";
import InviteAcceptPage from "./components/workspace/InviteAcceptPage";
import MobileOnboard from "./MobileOnboard";
import PrivacyPolicy from "./PrivacyPolicy";
import UnsubscribePage from "./UnsubscribePage";
import TermsOfUse from "./TermsOfUse";
import ReauthPage from "./components/auth/ReauthPage";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

// Fetch interceptor — injects X-Session-Token header and handles 401/session refresh.
(function installFetchInterceptor() {
  const _fetch = window.fetch;
  window.fetch = function(input, init) {
    const url = typeof input === "string" ? input : (input?.url || "");
    const isNectarApi = url.includes("go.brandhives.com") || url.startsWith("/app/");
    const isAdminRoute = url.includes("/app/api/admin/");
    const isAuthCheck = url.includes("/app/api/auth/me");

    if (isNectarApi) {
      const st = localStorage.getItem("nectar_st") || sessionStorage.getItem("nectar_st");
      if (st) {
        init = init || {};
        init.headers = { ...(init.headers || {}), "X-Session-Token": st };
      }
    }

    const promise = _fetch.call(this, input, init);

    if (isNectarApi && !isAdminRoute && !isAuthCheck) {
      return promise.then(response => {
        if (response.status === 401) {
          localStorage.removeItem("nectar_st");
          localStorage.removeItem("nectar_token_created");
          sessionStorage.removeItem("nectar_st");
          window.dispatchEvent(new CustomEvent("nectar:session-expired"));
        } else if (response.ok && localStorage.getItem("nectar_st")) {
          localStorage.setItem("nectar_token_created", String(Date.now()));
        }
        return response;
      });
    }

    return promise;
  };
})();

// On magic link redirect, st= is in the hash: #/dashboard?st=TOKEN
// Store it in localStorage so the fetch interceptor sends it on all API calls.
(function extractSessionToken() {
  // Token is in the hash: #/dashboard?st=TOKEN (never touches the server)
  const hash = window.location.hash;
  const qIdx = hash.indexOf("?");
  if (qIdx >= 0) {
    const st = new URLSearchParams(hash.slice(qIdx)).get("st");
    if (st) {
      localStorage.setItem("nectar_st", st);
      localStorage.setItem("nectar_token_created", String(Date.now()));
      sessionStorage.setItem("nectar_st", st);
      window.history.replaceState({}, "", window.location.pathname + hash.slice(0, qIdx));
    }
  } else {
    // Fallback: try query string
    const st = new URLSearchParams(window.location.search).get("st");
    if (st) {
      localStorage.setItem("nectar_st", st);
      localStorage.setItem("nectar_token_created", String(Date.now()));
      sessionStorage.setItem("nectar_st", st);
      window.history.replaceState({}, "", window.location.pathname + hash);
    }
  }
})();

// Shown briefly after LinkedIn OAuth redirect during new user onboarding.
// Reads pending LinkedIn data from Flask session, stashes in sessionStorage, then navigates to #/start.
function LinkedInConnectedBridge({ onDone }) {
  useEffect(() => {
    // Extract onboard_token from hash query params e.g. #/onboarding/linkedin-connected?onboard_token=UUID
    const hashQuery = window.location.hash.split("?")[1] || "";
    const onboardToken = new URLSearchParams(hashQuery).get("onboard_token") || "";
    const url = onboardToken
      ? `${API_BASE}/app/api/onboard/linkedin-session?onboard_token=${encodeURIComponent(onboardToken)}`
      : `${API_BASE}/app/api/onboard/linkedin-session`;
    fetch(url, { credentials: "include" })
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        if (onboardToken) sessionStorage.setItem("nectar_onboard_token", onboardToken);
        sessionStorage.setItem("nectar_li_connected", JSON.stringify({
          connected: data.connected || false,
          linkedin_name: data.linkedin_name || null,
          linkedin_profile_image: data.linkedin_profile_image || null,
        }));
        sessionStorage.removeItem("nectar_li_onboarding");
        onDone();
      })
      .catch(() => {
        sessionStorage.setItem("nectar_li_connected", JSON.stringify({ connected: false }));
        sessionStorage.removeItem("nectar_li_onboarding");
        onDone();
      });
  }, []); // eslint-disable-line
  return (
    <div style={{ background: "#F3F2EF", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 22, fontWeight: 700, color: "#0077B5" }}>
        Connecting LinkedIn···
      </div>
    </div>
  );
}

// ─── Busy Bee banner ──────────────────────────────────────────────────────────
function BusyBee({ onRetry }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 10, padding: "12px 18px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: "#92400E", margin: "12px 0" }}>
      <span style={{ fontSize: 20 }}>🐝</span>
      <span style={{ flex: 1 }}>Our bees are a little busy right now — hang tight and try again in a moment.</span>
      {onRetry && (
        <button onClick={onRetry} style={{ background: "#F59E0B", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
          Try Again
        </button>
      )}
    </div>
  );
}

// Simple hash-based routing: "#/start", "#/login", "#/dashboard", "#/settings"
// Default (no hash) = landing page
function getRoute() {
  const hash = window.location.hash;
  if (hash.startsWith("#/admin"))                            return "admin";
  if (hash.startsWith("#/mobile-start"))                      return "mobile-start";
  if (hash.startsWith("#/start"))                            return "start";
  if (hash.startsWith("#/reauth"))                            return "reauth";
  if (hash.startsWith("#/login"))                            return "login";
  if (hash.startsWith("#/dashboard"))                        return "dashboard";
  if (hash.startsWith("#/settings"))                         return "settings";
  if (hash.startsWith("#/onboarding/linkedin-connected"))    return "linkedin-connected";
  if (hash.startsWith("#/onboarding/linkedin-failed"))       return "linkedin-failed";
  if (hash.startsWith("#/paywall"))                          return "paywall";
  if (hash.startsWith("#/invite-onboarding"))                 return "invite-onboarding";
  if (hash.startsWith("#/invite"))                           return "invite";
  if (hash.startsWith("#/plug") || hash.startsWith("#/my-plug")) return "dashboard";
  if (hash.startsWith("#/team"))                             return "dashboard";
  if (hash.startsWith("#/workspace"))                        return "dashboard";
  if (hash.startsWith("#/pricing"))                           return "pricing";
  if (hash.startsWith("#/privacy-policy"))                   return "privacy-policy";
  if (hash.startsWith("#/unsubscribe"))                       return "unsubscribe";
  if (hash.startsWith("#/terms"))                            return "terms";
  return "landing";
}

const SESSION_MAX_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export default function App() {
  const [route, setRoute] = useState(getRoute);
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [busyBee, setBusyBee] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("presence");
  const [sessionExpired, setSessionExpired] = useState(false);

  // Listen for hash changes (back/forward navigation)
  useEffect(() => {
    const handler = () => setRoute(getRoute());
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  useEffect(() => {
    const handler = () => setBusyBee(true);
    window.addEventListener("nectar:busy-bee", handler);
    return () => window.removeEventListener("nectar:busy-bee", handler);
  }, []);

  // Handle session expiry from 401 interceptor
  useEffect(() => {
    const handler = () => {
      setIsAuthed(false);
      setSessionExpired(true);
      setShowLoginForm(true);
      window.location.hash = "#/login";
      setRoute("login");
    };
    window.addEventListener("nectar:session-expired", handler);
    return () => window.removeEventListener("nectar:session-expired", handler);
  }, []);

  // Check auth session on mount
  useEffect(() => {
    // 30-day inactivity timeout
    const created = parseInt(localStorage.getItem("nectar_token_created") || "0", 10);
    if (created && Date.now() - created > SESSION_MAX_MS) {
      localStorage.removeItem("nectar_st");
      localStorage.removeItem("nectar_token_created");
      setSessionExpired(true);
      setShowLoginForm(true);
      setAuthChecked(true);
      if (route === "dashboard" || route === "settings") {
        window.location.hash = "#/login";
        setRoute("login");
      }
      return;
    }

    fetch(`${API_BASE}/app/api/auth/me`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.user_id) {
          setIsAuthed(true);
          // If user lands on root while authed, send to dashboard
          if (route === "landing") {
            navigate("dashboard");
          }
        }
        setAuthChecked(true);
      })
      .catch(() => setAuthChecked(true));
  }, []); // eslint-disable-line

  function navigate(page) {
    window.location.hash = `#/${page}`;
    setRoute(page);
  }

  // Loading splash while auth check runs
  if (!authChecked) {
    return (
      <div style={{ background: "#F3F2EF", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 22, fontWeight: 700, color: "#0077B5" }}>BrandHives</div>
      </div>
    );
  }

  // Admin route — fully independent auth, bypass user auth check
  if (route === "admin") {
    // Require admin key stored in sessionStorage
    const storedAdminKey = sessionStorage.getItem("nectar_admin_key");
    if (!storedAdminKey) {
      // Prompt for key
      const key = window.prompt("Admin key required:");
      if (!key) {
        window.location.hash = "#/";
        return null;
      }
      sessionStorage.setItem("nectar_admin_key", key);
    }
    return <NectarAdmin />;
  }

  // Invite onboarding — no auth required, rendered directly (NOT through NectarApp to avoid 401 profile fetch)
  if (route === "invite-onboarding") {
    return <InviteOnboarding />;
  }

  // Invite accept — no auth required
  if (route === "invite") {
    return (
      <div style={{ background: "#F3F2EF", minHeight: "100vh", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ background: "#fff", borderBottom: "1px solid #e8e8e4", padding: "14px 28px", display: "flex", alignItems: "center", gap: 10 }}>
          <img src="https://public-sleekbio.b-cdn.net/media/hsDcuXM54xpQxU96FHMGz0xTTryD9RoFcKYxorBF.jpg" alt="BrandHives" width="32" height="32" style={{ borderRadius: 8 }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>BrandHives</span>
        </div>
        <InviteAcceptPage user={null} onAccepted={() => { window.location.hash = "/dashboard"; window.location.reload(); }} />
      </div>
    );
  }

  // Existing user came through phone signup — clean up and continue to dashboard
  if (route === "dashboard" && sessionStorage.getItem("nectar_mobile_signup_token")) {
    sessionStorage.removeItem("nectar_mobile_signup_token");
    sessionStorage.removeItem("nectar_li_onboarding");
  }

  // Protected routes
  if ((route === "dashboard" || route === "settings") && !isAuthed) {
    navigate("login");
    return null;
  }

  if (route === "mobile-start") {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.split("?")[1] || "");
    const signupToken = params.get("signup_token") || "";
    return (
      <MobileOnboard
        signupToken={signupToken}
        onComplete={() => {
          setIsAuthed(true);
          navigate("dashboard");
        }}
      />
    );
  }

  if (route === "start") {
    return (
      <NectarOnboard
        plan={selectedPlan}
        onComplete={() => {
          const liData = sessionStorage.getItem("nectar_li_connected");
          try { if (JSON.parse(liData)?.connected) sessionStorage.setItem("nectar_li_just_connected", "1"); } catch {}
          setIsAuthed(true);
          navigate("dashboard");
        }}
        onSignIn={() => navigate("login")}
      />
    );
  }

  if (route === "reauth") {
    return <ReauthPage />;
  }

  if (route === "login") {
    // Check for error param in hash: #/login?error=invalid_token
    const hashParams = new URLSearchParams(window.location.hash.split("?")[1] || "");
    const linkError = hashParams.get("error");

    if (!showLoginForm && (linkError === "invalid_token" || linkError === "expired_token" || linkError === "missing_token" || linkError === "session_error")) {
      return (
        <div style={{ background: "#F3F2EF", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
          <div style={{ maxWidth: 440, width: "100%", padding: "48px 24px", textAlign: "center" }}>
            <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 28, fontWeight: 700, color: "#0077B5", marginBottom: 32 }}>BrandHives</div>
            <div style={{ fontSize: 48, marginBottom: 20 }}>🔗</div>
            <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 26, lineHeight: 1.3, marginBottom: 12 }}>
              {linkError === "expired_token" ? "This link has expired." : "This link is no longer valid."}
            </div>
            <div style={{ fontSize: 15, color: "#666666", lineHeight: 1.7, marginBottom: 36 }}>
              {linkError === "expired_token"
                ? "Sign-in links expire after 30 minutes. Request a new one below."
                : "This link has already been used or is invalid. Request a new one below."}
            </div>
            <button
              onClick={() => { window.history.replaceState({}, "", window.location.pathname + "#/login"); setShowLoginForm(true); }}
              style={{ width: "100%", background: "#0077B5", color: "#F3F2EF", border: "none", borderRadius: 10, padding: "16px", fontSize: 16, cursor: "pointer", fontFamily: "'Playfair Display',Georgia,serif" }}
            >
              Get a new sign-in link →
            </button>
          </div>
        </div>
      );
    }
    return (
      <>
        {sessionExpired && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, background: "#FEF3C7", border: "none", borderBottom: "1px solid #FCD34D", padding: "10px 20px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: "#92400E", textAlign: "center" }}>
            Your session expired — please sign in again.
          </div>
        )}
        <NectarApp
          initialView="login"
          onAuthenticated={() => { setIsAuthed(true); setSessionExpired(false); navigate("dashboard"); }}
        />
      </>
    );
  }

  if (route === "dashboard" || route === "settings") {
    return (
      <NectarApp
        initialView={route}
        onAuthenticated={() => { setIsAuthed(true); navigate("dashboard"); }}
      />
    );
  }

  if (route === "linkedin-connected") {
    // Mobile onboarding: read pending session data and return to mobile-start
    const mobileToken = sessionStorage.getItem("nectar_mobile_signup_token");
    if (mobileToken && sessionStorage.getItem("nectar_li_onboarding")) {
      return <LinkedInConnectedBridge onDone={() => navigate(`mobile-start?signup_token=${mobileToken}`)} />;
    }
    // New-user onboarding: read pending session data and continue to #/start
    if (sessionStorage.getItem("nectar_li_onboarding")) {
      return <LinkedInConnectedBridge onDone={() => navigate("start")} />;
    }
    // Post-onboarding reconnect (existing user): show confirmation and go to dashboard
    return (
      <div style={{ background: "#F3F2EF", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ maxWidth: 480, width: "100%", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 24 }}>🎉</div>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 36, lineHeight: 1.2, marginBottom: 16 }}>
            LinkedIn connected via<br /><em style={{ color: "#0077B5" }}>BrandHives.</em>
          </div>
          <div style={{ fontSize: 16, color: "#666666", lineHeight: 1.7, marginBottom: 40 }}>
            Your posts will be published securely each week. You're all set.
          </div>
          <button
            onClick={() => { sessionStorage.setItem("nectar_li_just_connected", "1"); navigate("dashboard"); }}
            style={{ width: "100%", background: "#0077B5", color: "#F3F2EF", border: "none", borderRadius: 10, padding: "17px", fontSize: 16, cursor: "pointer", fontFamily: "'Playfair Display',Georgia,serif" }}
          >
            Go to my dashboard →
          </button>
        </div>
      </div>
    );
  }

  if (route === "paywall") {
    return (
      <div style={{ background: "#F3F2EF", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ maxWidth: 480, width: "100%", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>🍯</div>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 36, lineHeight: 1.2, marginBottom: 16 }}>
            Your trial has<br /><em style={{ color: "#0077B5" }}>ended.</em>
          </div>
          <div style={{ fontSize: 16, color: "#666666", lineHeight: 1.7, marginBottom: 12 }}>
            Your 30-day free trial is over. Subscribe to keep your LinkedIn presence running automatically.
          </div>
          <div style={{ fontSize: 28, fontFamily: "'Playfair Display',Georgia,serif", color: "#0077B5", marginBottom: 8 }}>Starting at $99/mo</div>
          <div style={{ fontSize: 13, color: "#999999", marginBottom: 36 }}>Cancel anytime</div>
          <button
            onClick={() => navigate("login")}
            style={{ width: "100%", background: "#0077B5", color: "#F3F2EF", border: "none", borderRadius: 10, padding: "17px", fontSize: 16, cursor: "pointer", fontFamily: "'Playfair Display',Georgia,serif", marginBottom: 12 }}
          >
            Upgrade my account →
          </button>
          <button
            onClick={() => navigate("dashboard")}
            style={{ width: "100%", background: "transparent", color: "#666666", border: "1px solid #E1E9EE", borderRadius: 10, padding: "14px", fontSize: 14, cursor: "pointer", marginBottom: 12 }}
          >
            Continue on free plan →
          </button>
          <button
            onClick={() => navigate("login")}
            style={{ width: "100%", background: "transparent", color: "#999999", border: "none", padding: "10px", fontSize: 13, cursor: "pointer" }}
          >
            Sign in instead
          </button>
        </div>
      </div>
    );
  }

  if (route === "linkedin-failed") {
    return (
      <div style={{ background: "#F3F2EF", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ maxWidth: 480, width: "100%", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 24 }}>⚠️</div>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 32, lineHeight: 1.2, marginBottom: 16 }}>
            We had trouble<br /><em style={{ color: "#0077B5" }}>connecting to LinkedIn.</em>
          </div>
          <div style={{ fontSize: 16, color: "#666666", lineHeight: 1.7, marginBottom: 40 }}>
            Something went wrong during authorization. You can try again — your account is saved.
          </div>
          <button
            onClick={() => { window.location.hash = "#/start"; }}
            style={{ width: "100%", background: "#0077B5", color: "#F3F2EF", border: "none", borderRadius: 10, padding: "17px", fontSize: 16, cursor: "pointer", fontFamily: "'Playfair Display',Georgia,serif", marginBottom: 12 }}
          >
            Try again →
          </button>
          <button
            onClick={() => navigate("dashboard")}
            style={{ width: "100%", background: "transparent", color: "#005885", border: "1px solid #005885", borderRadius: 10, padding: "14px", fontSize: 15, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}
          >
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  if (route === "pricing") {
    return (
      <NectarLanding
        onStart={(plan) => { setSelectedPlan(plan || "presence"); navigate("start"); }}
        onSignIn={() => navigate("login")}
        scrollTo="pricing"
      />
    );
  }

  if (route === "privacy-policy") return <PrivacyPolicy />;
  if (route === "unsubscribe") return <UnsubscribePage />;
  if (route === "terms") return <TermsOfUse />;

  // Default: landing page
  return (
    <>
      {busyBee && <BusyBee onRetry={() => setBusyBee(false)} />}
      <NectarLanding
        onStart={(plan) => { setSelectedPlan(plan || "presence"); navigate("start"); }}
        onSignIn={() => navigate("login")}
      />
    </>
  );
}
